import { Check, ChevronDown, X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { usePopoverAlign } from '../hooks/usePopoverAlign'
import { useListbox } from '../inputs/useListbox'

export interface FilterOption {
  value: string
  label: string
  icon?: ReactNode
}

export interface FilterMenuProps {
  label: string
  icon: ReactNode
  /** `null` means no filter ("All"). */
  value: string | null
  options: FilterOption[]
  onChange: (value: string | null) => void
  allLabel?: string
}

/**
 * Compact single-select filter (mockup `FilterMenu.tsx`), plus listbox keyboard support. The clear button sits
 * beside the trigger instead of inside it, so no interactive element is nested in another (Frontend §2.4).
 */
export function FilterMenu({ label, icon, value, options, onChange, allLabel = 'All' }: FilterMenuProps) {
  const [open, setOpenState] = useState(false)
  const { popRef, alignRight } = usePopoverAlign(open)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const baseId = useId()
  const listId = `${baseId}-list`
  const optionId = (i: number) => `${baseId}-opt-${String(i)}`

  const items: { value: string | null; label: string; icon?: ReactNode }[] = [
    { value: null, label: allLabel },
    ...options,
  ]
  const selectedIndex = items.findIndex((o) => o.value === value)

  const setOpen = (next: boolean) => {
    setOpenState(next)
    if (!next) triggerRef.current?.focus()
  }
  const choose = (v: string | null) => {
    onChange(v)
    setOpen(false)
  }

  const { active, setActive, onKeyDown } = useListbox({
    items,
    open,
    setOpen,
    selectedIndex,
    onChoose: (item) => {
      choose(item.value)
    },
  })

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpenState(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
    }
  }, [open])

  const current = options.find((o) => o.value === value)
  const activeId = open && active >= 0 ? optionId(active) : undefined

  return (
    <div className="date-filter" ref={ref}>
      <div className={`filter-trigger ${current ? 'active' : ''}`}>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          className="ft-main"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={activeId}
          aria-label={`${label}: ${current ? current.label : allLabel}`}
          onClick={() => {
            if (open) setOpen(false)
            else {
              setOpenState(true)
              setActive(selectedIndex >= 0 ? selectedIndex : 0)
            }
          }}
          onKeyDown={onKeyDown}
        >
          {icon}
          <span className="ft-label">{label}:</span> {current ? current.label : allLabel}
          {!current && <ChevronDown aria-hidden />}
        </button>
        {current && (
          <button
            type="button"
            className="ft-clear"
            aria-label={`Clear ${label} filter`}
            onClick={() => {
              onChange(null)
            }}
          >
            <X aria-hidden />
          </button>
        )}
      </div>
      {open && (
        <div
          className={`select-menu filter-menu ${alignRight ? 'align-right' : ''}`}
          role="listbox"
          id={listId}
          aria-label={label}
          ref={popRef}
        >
          {items.map((o, i) => (
            // Keyboard is handled on the combobox (aria-activedescendant); options are not tab stops.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              key={o.value ?? '__all__'}
              id={optionId(i)}
              className={`select-option${i === active ? ' active' : ''}`}
              role="option"
              tabIndex={-1}
              aria-selected={o.value === value}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
              onMouseEnter={() => {
                setActive(i)
              }}
              onClick={() => {
                choose(o.value)
              }}
            >
              {o.icon}
              <span className="grow">{o.label}</span>
              {o.value === value && <Check className="check" aria-hidden />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
